<template>
  <div class="ops-genre-tag-picker">
    <Poptip
      trigger="click"
      placement="bottom-start"
      transfer
      :width="360"
      padding="0"
      :disabled="disabled"
      @on-popper-hide="handlePopperHide"
    >
      <div
        class="ops-genre-tag-picker__control"
        :class="{ 'ops-genre-tag-picker__control--disabled': disabled }"
      >
        <div v-if="selectedTags.length" class="ops-genre-tag-picker__selected">
          <span
            v-for="tag in displayTags"
            :key="tagId(tag)"
            class="ops-genre-tag-picker__selected-tag"
          >
            {{ tag.tagName }}
            <button
              type="button"
              aria-label="移除内容标签"
              :disabled="disabled"
              @click.stop="removeTag(tagId(tag))"
            >
              <Icon type="ios-close" />
            </button>
          </span>
          <span
            v-if="hiddenCount > 0"
            class="ops-genre-tag-picker__selected-more"
            @click.stop="expanded = !expanded"
          >{{ expanded ? '收起' : '+' + hiddenCount }}</span>
        </div>
        <span v-else class="ops-genre-tag-picker__placeholder">
          {{ placeholder }}
        </span>
        <span class="ops-genre-tag-picker__suffix">
          {{ value.length }}/{{ limit }}
          <Icon type="ios-arrow-down" />
        </span>
      </div>

      <div slot="content" class="ops-genre-tag-picker__popup">
        <div class="ops-genre-tag-picker__search">
          <Input v-model="searchKeyword" placeholder="搜索内容标签" clearable>
            <Icon slot="prefix" type="ios-search" />
          </Input>
        </div>
        <div class="ops-genre-tag-picker__groups">
          <section
            v-for="group in filteredGroups"
            :key="group.name"
            class="ops-genre-tag-picker__group"
          >
            <h4>
              {{ group.name }}
              <span>({{ group.total }})</span>
            </h4>
            <div class="ops-genre-tag-picker__tags">
              <button
                v-for="tag in group.tags"
                :key="tagId(tag)"
                type="button"
                class="ops-genre-tag-picker__tag"
                :class="{ 'ops-genre-tag-picker__tag--active': isSelected(tagId(tag)) }"
                :disabled="isTagDisabled(tag) && !isSelected(tagId(tag))"
                :title="isTagDisabled(tag) ? '该标签已停用' : undefined"
                @click="toggleTag(tag)"
              >
                {{ tag.tagName }}
              </button>
            </div>
          </section>
          <div v-if="!filteredGroups.length" class="ops-genre-tag-picker__empty">
            暂无匹配的内容标签
          </div>
        </div>
      </div>
    </Poptip>
  </div>
</template>

<script>
export default {
  name: 'OpsGenreTagPicker',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    groups: {
      type: Array,
      default: () => []
    },
    limit: {
      type: Number,
      default: 10
    },
    maxTagCount: {
      type: Number,
      default: 2
    },
    placeholder: {
      type: String,
      default: '请选择内容标签'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchKeyword: '',
      expanded: false
    }
  },
  computed: {
    tagMap() {
      const map = {}
      ;(this.groups || []).forEach(group => {
        ;(group.tags || []).forEach(tag => {
          if (tag && this.tagId(tag) !== undefined) map[this.tagId(tag)] = tag
        })
      })
      return map
    },
    selectedTags() {
      return (this.value || []).map(id => this.tagMap[id]).filter(Boolean)
    },
    visibleTags() {
      return this.selectedTags.slice(0, this.maxTagCount)
    },
    hiddenCount() {
      return Math.max(this.selectedTags.length - this.visibleTags.length, 0)
    },
    displayTags() {
      return this.expanded ? this.selectedTags : this.visibleTags
    },
    filteredGroups() {
      const keyword = (this.searchKeyword || '').trim().toLowerCase()
      return (this.groups || []).reduce((acc, group) => {
        const tags = group.tags || []
        const name = group.name || ''
        const categoryMatched = name.toLowerCase().includes(keyword)
        const filteredTags = !keyword || categoryMatched
          ? tags
          : tags.filter(tag => (tag.tagName || '').toLowerCase().includes(keyword))
        if (filteredTags.length) {
          acc.push({ name, tags: filteredTags, total: tags.length })
        }
        return acc
      }, [])
    }
  },
  methods: {
    tagId(tag) {
      return tag.id !== undefined && tag.id !== null && tag.id !== ''
        ? tag.id
        : tag.tagId
    },
    isSelected(id) {
      return (this.value || []).includes(id)
    },
    isTagDisabled(tag) {
      return tag.status === 0 || tag.status === '0'
    },
    removeTag(id) {
      if (this.disabled) return
      this.$emit('input', (this.value || []).filter(item => item !== id))
    },
    toggleTag(tag) {
      if (this.disabled) return
      const id = this.tagId(tag)
      if (this.isSelected(id)) {
        this.removeTag(id)
        return
      }
      if (this.isTagDisabled(tag)) return
      if ((this.value || []).length >= this.limit) {
        this.$Message.warning(`内容标签最多选择 ${this.limit} 个`)
        return
      }
      this.$emit('input', [...(this.value || []), id])
    },
    handlePopperHide() {
      this.searchKeyword = ''
      this.expanded = false
    }
  }
}
</script>

<style scoped lang="less">
.ops-genre-tag-picker {
  display: block;
}

.ops-genre-tag-picker /deep/ .ivu-poptip {
  display: block;
}

.ops-genre-tag-picker /deep/ .ivu-poptip-rel {
  display: block;
}

.ops-genre-tag-picker__control {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 32px;
  padding: 4px 8px;
  color: #515a6e;
  background: #fff;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover,
  &:focus {
    border-color: #57a3f3;
    box-shadow: 0 0 0 2px rgba(45, 140, 240, 0.2);
    outline: none;
  }

  &--disabled {
    color: #c5c8ce;
    background: #f7f7f7;
    cursor: not-allowed;

    &:hover,
    &:focus {
      border-color: #dcdee2;
      box-shadow: none;
    }
  }
}

.ops-genre-tag-picker__selected {
  display: flex;
  flex: 1;
  flex-wrap: nowrap;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
}

.ops-genre-tag-picker__selected-tag {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 1px 8px;
  overflow: hidden;
  color: #515a6e;
  line-height: 20px;
  white-space: nowrap;
  text-overflow: ellipsis;
  background: #f5f7fa;
  border-radius: 2px;

  button {
    display: inline-flex;
    flex: none;
    align-items: center;
    margin-left: 6px;
    padding: 0;
    color: #c5c8ce;
    font-size: 14px;
    line-height: 1;
    background: none;
    border: 0;
    cursor: pointer;

    &:hover {
      color: #515a6e;
    }
  }
}

.ops-genre-tag-picker__selected-more {
  display: inline-flex;
  flex: none;
  align-items: center;
  padding: 1px 8px;
  color: #2d8cf0;
  line-height: 20px;
  background: #e8f3ff;
  border-radius: 2px;
  cursor: pointer;
}

.ops-genre-tag-picker__placeholder {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #c5c8ce;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ops-genre-tag-picker__suffix {
  display: inline-flex;
  flex: none;
  gap: 8px;
  align-items: center;
  margin-left: 8px;
  color: #c5c8ce;
  font-size: 13px;
}

.ops-genre-tag-picker__popup {
  display: flex;
  flex-direction: column;
  max-height: 427px;
  overflow: hidden;
  white-space: normal;
  background: #fff;
  border: 1px solid #dcdee2;
  border-radius: 4px;
}

.ops-genre-tag-picker__search {
  flex: none;
  padding: 16px;
  border-bottom: 1px solid #e8eaec;
}

.ops-genre-tag-picker__groups {
  flex: 1;
  min-height: 0;
  max-height: 360px;
  padding: 16px;
  overflow-y: auto;
}

.ops-genre-tag-picker__group {
  & + & {
    margin-top: 24px;
  }

  h4 {
    margin: 0 0 12px;
    color: #17233d;
    font-weight: 600;
    font-size: 14px;

    span {
      margin-left: 6px;
      color: #808695;
      font-weight: 400;
    }
  }
}

.ops-genre-tag-picker__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 12px;
}

.ops-genre-tag-picker__tag {
  padding: 5px 12px;
  color: #515a6e;
  line-height: 20px;
  background: #fff;
  border: 1px solid #dcdee2;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.1s cubic-bezier(0, 0, 1, 1);

  &:hover:not(:disabled),
  &--active {
    color: #2d8cf0;
    background: #e8f3ff;
    border-color: #2d8cf0;
  }

  &:disabled {
    color: #c5c8ce;
    background: #f7f7f7;
    cursor: not-allowed;
  }
}

.ops-genre-tag-picker__empty {
  padding: 32px 0;
  color: #808695;
  text-align: center;
}
</style>
